import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiGet, apiPatch, ApiError } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { DataTable } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { useModal } from '../contexts/ModalContext';

type FeatureVerdict = 'hot' | 'warm' | 'cold';

interface FeatureDetail {
  featureId: string;
  displayName: string;
  description: string | null;
  category: string | null;
  archived: boolean;
  adoption: number;
  retention: number;
  revenue: number;
  eventCount: number;
  verdict: FeatureVerdict;
  eventsByType: Array<{ eventType: string; count: number }>;
  dailyEventCounts: Array<{ date: string; count: number }>;
}

const VERDICT_LABEL: Record<FeatureVerdict, string> = {
  hot: '🔥 Hot',
  warm: '🟡 Warm',
  cold: '🧊 Cold',
};

const VERDICT_TONE: Record<FeatureVerdict, 'good' | 'warn' | 'bad'> = {
  hot: 'good',
  warm: 'warn',
  cold: 'bad',
};

export function FeatureAnalyticsDetailPage() {
  const { featureId = '' } = useParams<{ featureId: string }>();
  const { confirm } = useModal();
  const { data, loading, error, reload } = useApiResource(
    () => apiGet<FeatureDetail>(`/analytics/admin/features/${encodeURIComponent(featureId)}`),
    [featureId],
  );

  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const startEditing = () => {
    if (!data) return;
    setDisplayName(data.displayName);
    setDescription(data.description ?? '');
    setCategory(data.category ?? '');
    setSaveError(null);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await apiPatch(`/analytics/admin/features/${encodeURIComponent(featureId)}`, {
        displayName,
        description: description || undefined,
        category: category || undefined,
      });
      setEditing(false);
      reload();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleArchive = async () => {
    if (!data) return;
    const ok = await confirm({
      title: data.archived ? 'Restore feature' : 'Archive feature',
      message: data.archived
        ? `Restore "${data.displayName}" to the active analytics list?`
        : `Archive "${data.displayName}"? It stays measurable but moves out of the main list.`,
    });
    if (!ok) return;
    try {
      await apiPatch(`/analytics/admin/features/${encodeURIComponent(featureId)}`, { archived: !data.archived });
      reload();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Update failed');
    }
  };

  if (loading) return <p className="empty-state">Loading feature analytics…</p>;
  if (error) return <div className="error-banner">{error}</div>;
  if (!data) return null;

  return (
    <div>
      <div className="section-header">
        <div>
          <Link to="/analytics" className="form-hint">
            ← Back to Analytics
          </Link>
          <h1 style={{ marginTop: '0.5rem' }}>{data.displayName}</h1>
          {data.description && <p>{data.description}</p>}
        </div>
        <StatusBadge label={VERDICT_LABEL[data.verdict]} tone={VERDICT_TONE[data.verdict]} />
      </div>

      <div className="card-grid" style={{ marginBottom: '1.5rem' }}>
        <div className="form-card">
          <h2 style={{ fontSize: '0.95rem', margin: 0 }}>Last 30 days</h2>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
            <Metric label="Adoption" value={data.adoption} />
            <Metric label="Retention" value={`${data.retention}%`} />
            <Metric label="Revenue" value={`$${data.revenue.toFixed(2)}`} />
            <Metric label="Total events" value={data.eventCount} />
          </div>
        </div>

        <div className="form-card">
          <h2 style={{ fontSize: '0.95rem', margin: 0 }}>Settings</h2>
          {saveError && <div className="error-banner">{saveError}</div>}
          {editing ? (
            <>
              <label htmlFor="fa-name">Display name</label>
              <input id="fa-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              <label htmlFor="fa-category">Category</label>
              <input id="fa-category" value={category} onChange={(e) => setCategory(e.target.value)} />
              <label htmlFor="fa-description">Description</label>
              <textarea id="fa-description" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
              <div className="row-actions" style={{ marginTop: '0.5rem' }}>
                <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void save()}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)}>
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <div className="row-actions">
              <button type="button" className="btn" onClick={startEditing}>
                Edit details
              </button>
              <button type="button" className="btn" onClick={() => void toggleArchive()}>
                {data.archived ? 'Restore' : 'Archive'}
              </button>
            </div>
          )}
        </div>
      </div>

      <DataTable
        id="analytics-event-types"
        rows={data.eventsByType}
        empty="No events in this window."
        noun="event types"
        getRowKey={(row) => row.eventType}
        columns={[
          {
            key: 'eventType',
            header: 'Event type',
            value: (row) => row.eventType,
            render: (row) => <span style={{ fontFamily: 'monospace' }}>{row.eventType}</span>,
          },
          {
            key: 'count',
            header: 'Count (last 30 days)',
            value: (row) => row.count,
            align: 'right',
          },
        ]}
      />

      <DataTable
        id="analytics-daily"
        rows={data.dailyEventCounts}
        empty="No events in this window."
        noun="days"
        getRowKey={(row) => row.date}
        columns={[
          { key: 'date', header: 'Day', value: (row) => row.date },
          { key: 'count', header: 'Events', value: (row) => row.count, align: 'right' },
        ]}
      />
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="form-hint" style={{ margin: 0 }}>
        {label}
      </div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{value}</div>
    </div>
  );
}
